import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye, EyeOff, Mail, Lock, Shield } from 'lucide-react';
import ReCAPTCHA from 'react-google-recaptcha';
import { superAdminAuth } from '../api';
import Logo from './Logo';
import TwoFactorMethodSelector from './TwoFactorMethodSelector';

const SuperAdminLogin = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [requires2FA, setRequires2FA] = useState(false);
  const [verificationToken, setVerificationToken] = useState('');
  const [recaptchaToken, setRecaptchaToken] = useState(null);
  const [rememberMe, setRememberMe] = useState(false);
  const [deliveryMethod, setDeliveryMethod] = useState('email');
  const [smsEnabled, setSmsEnabled] = useState(false);
  const [codeDestination, setCodeDestination] = useState('');
  const recaptchaRef = useRef(null);
  const navigate = useNavigate();
  
  const recaptchaSiteKey = process.env.REACT_APP_RECAPTCHA_SITE_KEY || '';

  useEffect(() => {
    // Check if already logged in as super admin
    const checkAuth = async () => {
      try {
        const { developers } = await import('../api');
        const response = await developers.getMe();
        if (response.data.is_super_admin) {
          navigate('/super-admin');
        }
      } catch (e) {
        // Not logged in, stay on page
      }
    };
    checkAuth();
  }, [navigate]);

  useEffect(() => {
    // Only offer the SMS option when the server can actually send one
    import('../api')
      .then(({ developers }) => developers.get2FAConfig())
      .then((response) => setSmsEnabled(Boolean(response.data.sms_enabled)))
      .catch(() => setSmsEnabled(false));
  }, []);

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    
    if (recaptchaSiteKey && !recaptchaToken) {
      setError('Please complete the reCAPTCHA verification');
      return;
    }
    
    setLoading(true);
    setError('');

    try {
      const response = await superAdminAuth.login(email, password, recaptchaToken, rememberMe, deliveryMethod);
      if (response.data.requires_2fa) {
        setRequires2FA(true);
        setVerificationToken(response.data.verification_token);
        setCodeDestination(response.data.destination || email);
        setError('');
        // Reset reCAPTCHA when moving to 2FA
        if (recaptchaRef.current) {
          recaptchaRef.current.reset();
          setRecaptchaToken(null);
        }
      } else {
        // Login successful - force auth context to refresh
        window.location.href = '/super-admin';
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Login failed. Please check your credentials.');
      // Reset reCAPTCHA on error
      if (recaptchaRef.current) {
        recaptchaRef.current.reset();
        setRecaptchaToken(null);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleRecaptchaChange = (token) => {
    setRecaptchaToken(token);
    setError(''); // Clear any previous reCAPTCHA errors
  };

  const handle2FASubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      await superAdminAuth.verify2FA(verificationToken, code);
      // Login successful - force auth context to refresh
      window.location.href = '/super-admin';
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid verification code.');
      setLoading(false);
    }
  };

  if (requires2FA) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4" style={{ background: 'var(--gradient-secondary)' }}>
        <div className="w-full max-w-md">
          <div className="shadow-elegant border-border/50 bg-card/80 backdrop-blur-sm rounded-lg border p-0">
            <div className="space-y-2 text-center pt-5 pb-3 px-6">
              <Logo className="mx-auto mb-4" />
              <div className="flex items-center justify-center gap-2 mb-2">
                <Shield className="w-6 h-6 text-purple-500" />
                <h2 className="text-2xl font-bold text-foreground">Two-Factor Authentication</h2>
              </div>
              <p className="text-sm text-muted-foreground">
                {deliveryMethod === 'sms'
                  ? 'Enter the verification code sent to your phone'
                  : 'Enter the verification code sent to your email'}
              </p>
            </div>

            <form onSubmit={handle2FASubmit} className="px-6 pb-6 space-y-4">
              {error && (
                <div className="bg-destructive/10 border border-destructive/20 text-destructive px-4 py-3 rounded-lg text-sm">
                  {error}
                </div>
              )}

              <div className="bg-purple-500/10 border border-purple-500/20 rounded-lg p-4 mb-4">
                <p className="text-sm text-foreground">
                  A verification code has been sent to <strong>{codeDestination || email}</strong>
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  {deliveryMethod === 'sms'
                    ? 'Check your text messages and enter the 6-digit code below.'
                    : 'Please check your email and enter the 6-digit code below.'}
                </p>
              </div>

              <div className="space-y-2">
                <label htmlFor="code" className="text-sm font-medium text-foreground flex items-center gap-2">
                  <Shield className="w-4 h-4" />
                  Verification Code
                </label>
                <input
                  id="code"
                  type="text"
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  required
                  maxLength={6}
                  className="w-full px-4 py-2 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 text-foreground text-center text-2xl tracking-widest font-mono"
                  placeholder="000000"
                  disabled={loading}
                  autoFocus
                />
              </div>

              <button
                type="submit"
                disabled={loading || code.length !== 6}
                className="w-full py-2.5 bg-gradient-to-r from-purple-600 to-purple-700 text-white rounded-lg font-medium hover:from-purple-700 hover:to-purple-800 transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-purple-500/20"
              >
                {loading ? 'Verifying...' : 'Verify Code'}
              </button>

              <button
                type="button"
                onClick={() => {
                  setRequires2FA(false);
                  setCode('');
                  setVerificationToken('');
                  setCodeDestination('');
                  setError('');
                  // Reset reCAPTCHA when going back
                  if (recaptchaRef.current) {
                    recaptchaRef.current.reset();
                    setRecaptchaToken(null);
                  }
                }}
                className="w-full py-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
                disabled={loading}
              >
                Back to login
              </button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ background: 'var(--gradient-secondary)' }}>
      <div className="w-full max-w-md">
        <div className="shadow-elegant border-border/50 bg-card/80 backdrop-blur-sm rounded-lg border p-0">
          <div className="space-y-2 text-center pt-5 pb-3 px-6">
            <Logo className="mx-auto mb-4" />
            <div className="flex items-center justify-center gap-2 mb-2">
              <Shield className="w-6 h-6 text-purple-500" />
              <h2 className="text-2xl font-bold text-foreground">Super Admin Login</h2>
            </div>
            <p className="text-sm text-muted-foreground">Sign in with super admin credentials</p>
          </div>

          <form onSubmit={handlePasswordSubmit} className="px-6 pb-6 space-y-4">
            {error && (
              <div className="bg-destructive/10 border border-destructive/20 text-destructive px-4 py-3 rounded-lg text-sm">
                {error}
              </div>
            )}

            <div className="space-y-2">
              <label htmlFor="email" className="text-sm font-medium text-foreground flex items-center gap-2">
                <Mail className="w-4 h-4" />
                Email
              </label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="w-full px-4 py-2 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 text-foreground"
                placeholder="superadmin@example.com"
                disabled={loading}
              />
            </div>

            <div className="space-y-2">
              <label htmlFor="password" className="text-sm font-medium text-foreground flex items-center gap-2">
                <Lock className="w-4 h-4" />
                Password
              </label>
              <div className="relative">
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="w-full px-4 py-2 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 text-foreground pr-10"
                  placeholder="Enter your password"
                  disabled={loading}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  disabled={loading}
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            {/* reCAPTCHA */}
            {recaptchaSiteKey && (
              <div className="flex justify-center">
                <ReCAPTCHA
                  ref={recaptchaRef}
                  sitekey={recaptchaSiteKey}
                  onChange={handleRecaptchaChange}
                  onExpired={() => setRecaptchaToken(null)}
                  onError={() => {
                    setRecaptchaToken(null);
                    setError('reCAPTCHA error. Please try again.');
                  }}
                />
              </div>
            )}

            <TwoFactorMethodSelector
              value={deliveryMethod}
              onChange={setDeliveryMethod}
              disabled={loading}
              accent="purple"
              smsEnabled={smsEnabled}
            />

            <label className="flex items-center gap-2.5 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
                disabled={loading}
                className="w-4 h-4 rounded border-border accent-purple-600 cursor-pointer"
              />
              <span className="text-sm text-muted-foreground">Remember this device for 30 days</span>
            </label>

            <button
              type="submit"
              disabled={loading || (recaptchaSiteKey && !recaptchaToken)}
              className="w-full py-2.5 bg-gradient-to-r from-purple-600 to-purple-700 text-white rounded-lg font-medium hover:from-purple-700 hover:to-purple-800 transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-purple-500/20"
            >
              {loading ? 'Logging in...' : 'Sign In as Super Admin'}
            </button>

            <div className="text-center text-sm text-muted-foreground">
              <p>Not a super admin?</p>
              <p className="mt-1">
                <a 
                  href="/developer/login" 
                  className="text-primary hover:underline font-medium"
                >
                  Developer Login
                </a>
              </p>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default SuperAdminLogin;

